import { ElementRef, inject, Injectable } from '@angular/core'
import { Zone } from '../model/zone'
import Map from 'ol/Map'
import { fromLonLat, Projection, transform } from 'ol/proj'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  startWith,
  Subject,
} from 'rxjs'
import { ToolEnum } from '../model/tool.enum'
import { Geometry, LineString, Point, Polygon } from 'ol/geom'
import {
  CoordinateDto,
  PanneauDto,
  PolygonDto,
  RaccordementPublic,
  TrenchCoordinate,
  TrenchLine,
  TrenchLineTypeEnum,
  TrenchTableRow,
  TrenchTypeEnum,
  ZoneMeasures,
} from '../model/zone-measures'
import VectorSource from 'ol/source/Vector'
import { Draw, Modify, Select } from 'ol/interaction'
import { InteractionUtils } from '../utils/interaction-utils'
import { MeasureUtils } from '../utils/measure.utils'
import { MapUtils } from '../utils/map.utils'
import { MapBrowserEvent, Overlay } from 'ol'
import Feature, { FeatureLike } from 'ol/Feature'
import { TooltipUtils } from '../utils/tooltip.utils'
import { GeocodingService } from '../data-access/http/geocoding.service'
import { ReverseGeoCodeGoogleDto } from '../model/address-google-dto'
import { SessionZoneSaveDto } from 'src/app/sunleon/data-access/http/dto/session-zone-save-dto'
import VectorLayer from 'ol/layer/Vector'
import { Fill, Stroke, Style, Text } from 'ol/style'
import TileLayer from 'ol/layer/Tile'
import { StyleUtils } from '../utils/style.utils'

@Injectable({ providedIn: 'root' })
export class GeomapDomainService {
  state: BehaviorSubject<GeomapStateModel> = new BehaviorSubject<GeomapStateModel>({
    map: MapUtils.initMap(false),
    zones: [new Zone(null, null), new Zone(null, null), new Zone(null, null), new Zone(null, null), new Zone(null, null), new Zone(null, null), new Zone(null, null)],
    activeZoneId: 0,
    activeTool: ToolEnum.None,
    selectedModeValorisation: false,
    selectedTypePose: [false, false, false, false, false, false],
    selectedBarreOutils: false,
    selectedDessinOutilsSeulementZone: false,
    isEnedisEnabled: false,
    isHtaEnabled: false,
    isPostesEnabled: false,
    isPostesHtaHtaEnabled: false,
    selectedRaccordementType: 'BT',
    counterPlacementActive: false,
    maxCounterCount: 0,
    trenchSelectionMode: null,
    pendingTrenchZoneIds: [],
    pendingTrenchIsPdl: false,
    validatedTrenchMode: null,
    tableSeedLabels: [],
    activeTrenchLabel: null,
    manualTrenchRows: {},
    isCadastreEnabled: false,
  })

  private geocodingService = inject(GeocodingService)
    private cadastreLayer: TileLayer<any> | null = null

  private distanceMeasureSource: VectorSource = new VectorSource();
  private distanceMeasureLayer: VectorLayer<any>;

  // Selectors
  activeZoneId$: Observable<number> = this.state.asObservable().pipe(
    map((state) => state.activeZoneId),
    distinctUntilChanged()
  )
  activeTool$: Observable<ToolEnum> = this.state.asObservable().pipe(
    map((state) => state.activeTool),
    distinctUntilChanged()
  )

  selectedModeValorisation$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.selectedModeValorisation),
    distinctUntilChanged()
  )

  selectedTypePoseCurrentZone$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => {
      const selectedTypePoseIndex = this.getSelectedTypePoseIndex(this.activeZoneId)
      return selectedTypePoseIndex === null ? false : !!state.selectedTypePose[selectedTypePoseIndex]
    }),
    distinctUntilChanged()
  )

  selectedOutils$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.selectedBarreOutils),
    distinctUntilChanged()
  )

  selectedDessinOutilsSeulementZone$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.selectedDessinOutilsSeulementZone),
    distinctUntilChanged()
  )

  isEnedisEnabled$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.isEnedisEnabled),
    distinctUntilChanged()
  );

  isHtaEnabled$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.isHtaEnabled),
    distinctUntilChanged()
  );

  isPostesEnabled$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.isPostesEnabled),
    distinctUntilChanged()
  );

  isPostesHtaHtaEnabled$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.isPostesHtaHtaEnabled),
    distinctUntilChanged()
  );

  selectedRaccordementType$: Observable<'BT' | 'HT'> = this.state.asObservable().pipe(
    map((state) => state.selectedRaccordementType),
    distinctUntilChanged()
  )

  counterPlacementActive$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.counterPlacementActive),
    distinctUntilChanged()
  )

  trenchSelectionMode$: Observable<'private' | 'public' | 'ptr' | null> = this.state.asObservable().pipe(
    map((state) => state.trenchSelectionMode),
    distinctUntilChanged()
  )

  pendingTrenchZoneIds$: Observable<number[]> = this.state.asObservable().pipe(
    map((state) => state.pendingTrenchZoneIds),
    distinctUntilChanged()
  )

  pendingTrenchIsPdl$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.pendingTrenchIsPdl),
    distinctUntilChanged()
  )

  validatedTrenchMode$: Observable<'private' | 'public' | 'ptr' | null> = this.state.asObservable().pipe(
    map((state) => state.validatedTrenchMode),
    distinctUntilChanged()
  )

  activeTrenchLabel$: Observable<string | null> = this.state.asObservable().pipe(
    map((state) => state.activeTrenchLabel),
    distinctUntilChanged()
  )

  isCadastreEnabled$: Observable<boolean> = this.state.asObservable().pipe(
    map((state) => state.isCadastreEnabled),
    distinctUntilChanged()
  );

  currentZoneHasBeanReset$: Subject<number> = new Subject<number>()
  // Perf calepinage (retour client) : tant qu'un dessin est SÉLECTIONNÉ pour édition (interaction
  // Select), sa géométrie émet un 'change' en continu pendant qu'on déplace les sommets. On gèle
  // le recalcul tant que isEditingDrawing est vrai, et on le relance une seule fois quand on
  // DÉSÉLECTIONNE le dessin (clic sur une zone vide de la carte → editCommitted$). Le dessin neuf
  // (Draw/addFeature) et les mutations programmatiques (clear/restauration) passent normalement.
  private isEditingDrawing = false
  private editCommitted$ = new Subject<void>()
  // Largeurs de dessin des ombrières VL (mètres) = nb_modules × LONGUEUR_PANNEAU × cos(inclinaison),
  // calculées et poussées par CalepinageService via setOmbriereWidths (retour client 11041).
  width1place = 0;
  width2place = 0;
  widthEstOuest = 0;
  private raccordementPublicSource: VectorSource = new VectorSource();
 private raccordementPublicLayer: any;

  constructor() {
    this.setZone(0)
    const bodyStyles = window.getComputedStyle(document.body)
    const resolveColor = (cssVar: string, fallback: string): string => {
      const value = bodyStyles.getPropertyValue(cssVar)?.trim()
      return value || fallback
    }
    this.state.next({
      ...this.state.value,
      zones: [
        new Zone(resolveColor('--colorSurfaceZone1', '#F9D38A'), resolveColor('--colorZone1', '#FF5A36')),
        new Zone(resolveColor('--colorSurfaceZone2', '#A7D7A8'), resolveColor('--colorZone2', '#5FAD56')),
        new Zone(resolveColor('--colorSurfaceZone3', '#F0C2C2'), resolveColor('--colorZone3', '#D95D74')),
        new Zone(
          resolveColor('--colorSurfaceModeValorisation', '#DDDDDD'),
          resolveColor('--colorModeValorisation', '#000000')
        ),
        new Zone(resolveColor('--colorSurfaceZone4', '#D9D9D9'), resolveColor('--colorZone4', '#8F8F8F')),
        new Zone(resolveColor('--colorSurfaceZone5', '#D8B48A'), resolveColor('--colorZone5', '#8B5A2B')),
        new Zone(resolveColor('--colorSurfaceZone6', '#F2C6E0'), resolveColor('--colorZone6', '#E89AC6')),
      ],
    })

    this.raccordementPublicLayer = new VectorLayer({
      source: this.raccordementPublicSource,
      style: InteractionUtils.buildDefaultStyleFromColor('#000000'),
    });
    this.map.addLayer(this.raccordementPublicLayer);
        this.distanceMeasureLayer = new VectorLayer({
      source: this.distanceMeasureSource,
      style: (feature: FeatureLike): Style => {
        const geometry = feature.getGeometry();
        if (!(geometry instanceof LineString)) {
          return new Style();
        }
        const length = MeasureUtils.computeLength(geometry);
        return new Style({
          stroke: new Stroke({
            color: '#000000ff',
            width: 5,
            lineDash: [8, 11],
          }),
          text: new Text({
            text: `${length.toFixed(2)} m`,
            font: 'bold 15px Arial',
            fill: new Fill({ color: '#000000' }),
            stroke: new Stroke({ color: '#ffffff', width: 3 }),
            offsetY: -10,
            placement: 'line',
            overflow: true,
          }),
        });
      },
    });
    this.map.addLayer(this.distanceMeasureLayer);
    // Les largeurs d'ombrière (ex-paramètres LARGEUR_OMB_*) sont désormais calculées en nombre
    // de modules et poussées par CalepinageService (voir setOmbriereWidths) — retour client 11041.
  }

  /**
   * Largeurs de dessin des ombrières VL (mètres), poussées par CalepinageService :
   * largeur = nb_modules × LONGUEUR_PANNEAU × cos(inclinaison). Consommées par
   * activateMeasureAreaTool pour épaissir le tracé en rectangle.
   */
  setOmbriereWidths(widths: { width1place: number; width2place: number; widthEstOuest: number }): void {
    this.width1place = widths.width1place
    this.width2place = widths.width2place
    this.widthEstOuest = widths.widthEstOuest
  }

  get map(): Map {
    return this.state.value.map
  }

  get currentZone(): Zone {
    return this.state.value.zones[this.state.value.activeZoneId]
  }

  get activeZoneId(): number {
    return this.state.value.activeZoneId
  }

  getZone(zoneIndex: number): Zone | null {
    return this.state.value.zones[zoneIndex] ?? null
  }

  get activeTool(): ToolEnum {
    return this.state.value.activeTool
  }

  getGpsData$ = (): Observable<number[]> =>
    fromEvent(this.state.value.zones[0].vectorLayer.getSource(), 'change').pipe(
      map((event: any) =>
        (
          (
            (event.target as VectorSource)
              .getFeatures()
              .find((feature) => feature.getGeometry().getType() === 'Polygon')
              ?.getGeometry() as Polygon
          )
            ?.getInteriorPoint()
            .transform(new Projection({ code: 'EPSG:3857' }), new Projection({ code: 'EPSG:4326' })) as Point
        )?.getCoordinates()
      ),
      startWith([0, 0])
    )

  /**
   * Flux 'change' d'une source MAIS gelé tant qu'un dessin est sélectionné pour édition : les
   * 'change' intermédiaires (déplacement de sommets) sont filtrés, et une émission unique est
   * rejouée à la désélection du dessin (editCommitted$).
   * → le calepinage ne se relance plus à chaque déplacement de sommet, seulement quand on clique
   *   dans le vide pour sortir de l'édition du dessin.
   */
  private committedSourceChange$ = (source: VectorSource): Observable<{ target: VectorSource }> =>
    merge(
      fromEvent(source, 'change').pipe(filter(() => !this.isEditingDrawing)),
      this.editCommitted$.pipe(map(() => ({ target: source }))),
    ) as Observable<{ target: VectorSource }>

  getAreaForZone$ = (zoneId: number): Observable<number> =>
    this.committedSourceChange$(this.state.value.zones[zoneId].vectorLayer.getSource()).pipe(
      map((event: any) => {
        const surfaces = (event.target as VectorSource)
          .getFeatures()
          .map((feature) => feature.getGeometry())
          .filter((geometry) => geometry.getProperties()['type'] === ToolEnum.Surface)
        return surfaces.reduce((acc, area) => acc + MeasureUtils.computeArea(area as Polygon), 0)
      }),
      startWith(null)
    )

  getAzimuthForZone$ = (zoneId: number): Observable<number> =>
    this.committedSourceChange$(this.state.value.zones[zoneId].vectorLayer.getSource()).pipe(
      map((event: any) => {
        const [azimuthDraw] = (event.target as VectorSource)
          .getFeatures()
          .map((feature) => feature.getGeometry())
          .filter((geometry) => geometry.getProperties()['type'] === ToolEnum.Azimuth)
        if (azimuthDraw) {
          return MeasureUtils.computeAzimuth(azimuthDraw as LineString)
        }
        return null
      }),
      startWith(null)
    )

  getExclusionsChange$ = (zoneId: number): Observable<unknown> =>
    this.committedSourceChange$(this.state.value.zones[zoneId].exclusionLayer.getSource()).pipe(startWith(null))

  getDistance$ = (): Observable<RaccordementPublic> =>
    fromEvent(this.state.value.zones[3].vectorLayer.getSource(), 'change').pipe(
      map((event: any) => {
        const vectorSource = event.target as VectorSource;
        const lineFeatures = vectorSource.getFeatures().filter((feature) => feature.getGeometry() instanceof LineString);
        const polygonFeatures = vectorSource.getFeatures().filter((feature) => feature.getGeometry() instanceof Polygon);

        const getCoordinatesForTool = (toolType: ToolEnum): PolygonDto[] =>
          lineFeatures
            .filter((feature) => feature.getGeometry().getProperties()['type'] === toolType)
            .map((feature) => {
              const lineGeometry = feature.getGeometry() as LineString;
              const coords = lineGeometry.getCoordinates().map(coord => {
                const [x, y] = transform(coord, 'EPSG:3857', 'EPSG:4326');
                return { x, y } as CoordinateDto;
              });
              return { coordinates: coords } as PolygonDto;
            });

        const roundDistance = (value: number): number => Math.round(value * 100) / 100;

        const computeTotalDistanceForToolAndZone = (toolType: ToolEnum, zoneId: number): number =>
          roundDistance(
            lineFeatures
              .filter((feature) =>
                feature.getGeometry().getProperties()['type'] === toolType &&
                feature.getGeometry().getProperties()['zoneId'] === zoneId
              )
              .reduce((acc, feature) => acc + MeasureUtils.computeLength(feature.getGeometry() as LineString), 0)
          );

        const computeTotalDistance = (coordinates: PolygonDto[]): number =>
          roundDistance(
            coordinates
              .filter(p => p.coordinates.length >= 2)
              .reduce((acc, polygon) => {
                const geometry = new LineString(polygon.coordinates.map(coord => fromLonLat([coord.x, coord.y])));
                return acc + MeasureUtils.computeLength(geometry);
              }, 0)
          );

        const getPolygonForTool = (toolType: ToolEnum): PolygonDto | null => {
          const feature = polygonFeatures.find((item) => item.getGeometry().getProperties()['type'] === toolType);
          if (!feature) {
            return null;
          }

          const geometry = feature.getGeometry() as Polygon;

          return {
            coordinates: geometry.getCoordinates()[0].map(coord => {
              const [x, y] = transform(coord, 'EPSG:3857', 'EPSG:4326');
              return { x, y } as CoordinateDto;
            }),
          };
        };

        const getCounterCenters = (): CoordinateDto[] =>
          polygonFeatures
            .filter((item) => {
              const type = item.getGeometry().getProperties()['type']
              return type === ToolEnum.MeterBt || type === ToolEnum.TransformerHt
            })
            .map((item) => {
              const center = (item.getGeometry() as Polygon).getInteriorPoint().getCoordinates()
              const [x, y] = transform(center, 'EPSG:3857', 'EPSG:4326')
              return { x, y } as CoordinateDto
            })

        const voirieEnedisCoordinates = getCoordinatesForTool(ToolEnum.EnedisLine);
        const trancheeEnrobeeCoordinates = getCoordinatesForTool(ToolEnum.PrivateTrenchEnrobee);
        const trancheeTerrainNaturelCoordinates = getCoordinatesForTool(ToolEnum.PrivateTrenchNaturel);
        const trancheeToitureCoordinates = getCoordinatesForTool(ToolEnum.PrivateTrenchToiture);

        return {
          distanceVoirieEnedis: computeTotalDistance(voirieEnedisCoordinates),
          voirieEnedisCoordinates,
          totalTrancheeEnrobee: computeTotalDistance(trancheeEnrobeeCoordinates),
          trancheeEnrobeeCoordinates,
          totalTrancheeTerrainNaturel: computeTotalDistance(trancheeTerrainNaturelCoordinates),
          trancheeTerrainNaturelCoordinates,
          totalTrancheeToiture: computeTotalDistance(trancheeToitureCoordinates),
          trancheeToitureCoordinates,
          compteursCoordinates: getCounterCenters(),
          positionPosteTransfoCoordinates: getPolygonForTool(ToolEnum.TransformerHt),
          distanceTrancheeEnrobe1: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchEnrobee, 1),
          distanceTrancheeTerrainNaturelle1: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchNaturel, 1),
          distanceTrancheePassagedecable1: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchToiture, 1),
          distanceTrancheeEnrobe2: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchEnrobee, 2),
          distanceTrancheeTerrainNaturelle2: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchNaturel, 2),
          distanceTrancheePassagedecable2: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchToiture, 2),
          distanceTrancheeEnrobe3: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchEnrobee, 3),
          distanceTrancheeTerrainNaturelle3: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchNaturel, 3),
          distanceTrancheePassagedecable3: computeTotalDistanceForToolAndZone(ToolEnum.PrivateTrenchToiture, 3),
        };
      }),
      startWith({
        distanceVoirieEnedis: 0,
        voirieEnedisCoordinates: [],
        totalTrancheeEnrobee: 0,
        trancheeEnrobeeCoordinates: [],
        totalTrancheeTerrainNaturel: 0,
        trancheeTerrainNaturelCoordinates: [],
        totalTrancheeToiture: 0,
        trancheeToitureCoordinates: [],
        compteursCoordinates: [],
        positionPosteTransfoCoordinates: null,
        distanceTrancheeEnrobe1: 0,
        distanceTrancheeTerrainNaturelle1: 0,
        distanceTrancheePassagedecable1: 0,
        distanceTrancheeEnrobe2: 0,
        distanceTrancheeTerrainNaturelle2: 0,
        distanceTrancheePassagedecable2: 0,
        distanceTrancheeEnrobe3: 0,
        distanceTrancheeTerrainNaturelle3: 0,
        distanceTrancheePassagedecable3: 0,
      })
    );

  getTrenchTableRows$ = (): Observable<TrenchTableRow[]> =>
    combineLatest([
      this.state.asObservable().pipe(map((state) => state.tableSeedLabels), distinctUntilChanged()),
      this.state.asObservable().pipe(map((state) => state.manualTrenchRows), distinctUntilChanged()),
      fromEvent(this.state.value.zones[3].vectorLayer.getSource(), 'change').pipe(startWith(null)),
    ]).pipe(
      map(([seedLabels, manualTrenchRows]): TrenchTableRow[] => {
        const rows: { [key: string]: TrenchTableRow } = {}
        const features = this.state.value.zones[3].vectorLayer.getSource().getFeatures()
        const roundDistance = (value: number): number => Math.round(value * 100) / 100

        seedLabels.forEach((label: string) => {
          rows[label] = { zoneLabel: label, enrobee: 0, terrainNaturel: 0, passageToiture: 0 }
        })

        features.forEach((feature) => {
          const geometry = feature.getGeometry()
          if (!(geometry instanceof LineString)) {
            return
          }

          const type = geometry.getProperties()['type']
          const targetLabel = geometry.getProperties()['targetLabel'] as string | undefined
          if (!targetLabel) {
            return
          }

          const length = MeasureUtils.computeLength(geometry)
          const currentRow = rows[targetLabel] || { zoneLabel: targetLabel, enrobee: 0, terrainNaturel: 0, passageToiture: 0 }
          if (type === ToolEnum.PrivateTrenchEnrobee || type === ToolEnum.EnedisLine) {
            currentRow.enrobee += length
          }
          if (type === ToolEnum.PrivateTrenchNaturel) {
            currentRow.terrainNaturel += length
          }
          if (type === ToolEnum.PrivateTrenchToiture) {
            currentRow.passageToiture += length
          }
          rows[targetLabel] = currentRow
        })

        Object.keys(manualTrenchRows).forEach((label) => {
          const manualRow = manualTrenchRows[label]
          const currentRow = rows[label] || { zoneLabel: label, enrobee: 0, terrainNaturel: 0, passageToiture: 0 }
          rows[label] = {
            zoneLabel: label,
            enrobee: manualRow.enrobee != null ? roundDistance(Number(manualRow.enrobee)) : currentRow.enrobee,
            terrainNaturel: manualRow.terrainNaturel != null ? roundDistance(Number(manualRow.terrainNaturel)) : currentRow.terrainNaturel,
            passageToiture: manualRow.passageToiture != null ? roundDistance(Number(manualRow.passageToiture)) : currentRow.passageToiture,
          }
        })

        return Object.keys(rows).map((key) => ({
          zoneLabel: rows[key].zoneLabel,
          enrobee: roundDistance(rows[key].enrobee),
          terrainNaturel: roundDistance(rows[key].terrainNaturel),
          passageToiture: roundDistance(rows[key].passageToiture),
        }))
      })
    )

  getCompteursCoordinatesSnapshot(): CoordinateDto[] {
    return this.state.value.zones[3].vectorLayer
      .getSource()
      .getFeatures()
      .filter((feature) => {
        const type = feature.getGeometry()?.getProperties()['type']
        return type === ToolEnum.MeterBt || type === ToolEnum.TransformerHt
      })
      .map((feature) => {
        const center = (feature.getGeometry() as Polygon).getInteriorPoint().getCoordinates()
        const [x, y] = transform(center, 'EPSG:3857', 'EPSG:4326')
        return { x, y } as CoordinateDto
      })
  }

  getVoirieEnedisCoordinatesSnapshot(): PolygonDto[] {
    return this.state.value.zones[3].vectorLayer
      .getSource()
      .getFeatures()
      .filter((feature) => feature.getGeometry()?.getProperties()['type'] === ToolEnum.EnedisLine)
      .map((feature) => {
        const geometry = feature.getGeometry() as LineString
        return {
          coordinates: geometry.getCoordinates().map((coord) => {
            const [x, y] = transform(coord, 'EPSG:3857', 'EPSG:4326')
            return { x, y } as CoordinateDto
          }),
        } as PolygonDto
      })
  }

  getPanneauxSnapshot(zoneIndex: number): PanneauDto[] {
    const zone = this.state.value.zones[zoneIndex]
    if (!zone) {
      return []
    }
    return zone.panneauLayer
      .getSource()
      .getFeatures()
      .filter((feature) => feature.getGeometry()?.getType() === 'Polygon')
      .map((feature) => this.featureToPanneauDto(feature))
  }

  private featureToPanneauDto(feature: Feature): PanneauDto {
    const geometry = feature.getGeometry() as Polygon
    const coordinates = geometry.getCoordinates()[0].map((coord) => {
      const [x, y] = transform(coord, 'EPSG:3857', 'EPSG:4326')
      return { x, y } as CoordinateDto
    })
    const props = feature.getProperties()
    return {
      id: (feature.getId() ?? props['id'] ?? '').toString(),
      blocId: (props['blocId'] ?? null) as number | null,
      coordinates,
    } as PanneauDto
  }

  private panneauDtoToFeature(dto: PanneauDto): Feature {
    const coordinates = dto.coordinates.map((coord: CoordinateDto) => fromLonLat([coord.x, coord.y]))
    const feature = new Feature({
      geometry: new Polygon([coordinates]),
    })
    feature.setId(dto.id)
    feature.setProperties({ type: ToolEnum.Panneau, blocId: dto.blocId, id: dto.id })
    return feature
  }

  getTrenchCoordinatesSnapshot(): TrenchLine[] {
    const features = this.state.value.zones[3].vectorLayer.getSource().getFeatures()
    const groupedByLabel: { [key: string]: LineString[] } = {}
    const manualTrenchRows = this.state.value.manualTrenchRows

    features.forEach((feature) => {
      const geometry = feature.getGeometry()
      if (!(geometry instanceof LineString)) {
        return
      }

      const targetLabel = geometry.getProperties()['targetLabel'] as string | undefined
      const toolType = geometry.getProperties()['type'] as ToolEnum | undefined
      if (!targetLabel || !toolType) {
        return
      }

      if (!groupedByLabel[targetLabel]) {
        groupedByLabel[targetLabel] = []
      }
      groupedByLabel[targetLabel].push(geometry)
    })

    const labels = Array.from(new Set([...Object.keys(groupedByLabel), ...Object.keys(manualTrenchRows)]))

    return labels.map((targetLabel) => {
      const geometries = groupedByLabel[targetLabel] ?? []
      const trenchCoordinates = geometries.reduce((acc: TrenchCoordinate[], geometry) => {
        const trenchType = this.getTrenchTypeEnum(geometry.getProperties()['type'] as ToolEnum)
        const existing = acc.find((item) => item.type === trenchType)
        const polygon = {
          coordinates: geometry.getCoordinates().map((coord) => {
            const [x, y] = transform(coord, 'EPSG:3857', 'EPSG:4326')
            return { x, y } as CoordinateDto
          }),
        }
        if (existing) {
          existing.polygons.push(polygon)
        } else {
          acc.push({ type: trenchType, polygons: [polygon] })
        }
        return acc
      }, [])

      const sumByToolType = (toolType: ToolEnum): number | null => {
        const total = geometries
          .filter((geometry) => geometry.getProperties()['type'] === toolType)
          .reduce((acc, geometry) => acc + MeasureUtils.computeLength(geometry), 0)
        return total > 0 ? Math.round(total * 100) / 100 : null
      }

      const manualRow = manualTrenchRows[targetLabel]

      return {
        type: this.trenchLabelToTypes(targetLabel),
        polygons: trenchCoordinates,
        enrobee: manualRow?.enrobee != null ? manualRow.enrobee : sumByToolType(ToolEnum.PrivateTrenchEnrobee),
        terrainNaturel: manualRow?.terrainNaturel != null ? manualRow.terrainNaturel : sumByToolType(ToolEnum.PrivateTrenchNaturel),
        passageToiture: manualRow?.passageToiture != null ? manualRow.passageToiture : sumByToolType(ToolEnum.PrivateTrenchToiture),
      } as TrenchLine
    })
  }

  getCounterCount$ = (): Observable<number> =>
    fromEvent(this.state.value.zones[3].vectorLayer.getSource(), 'change').pipe(
      map((event: any) =>
        (event.target as VectorSource)
          .getFeatures()
          .filter((feature) => {
            const type = feature.getGeometry()?.getProperties()['type']
            return type === ToolEnum.MeterBt || type === ToolEnum.TransformerHt
          }).length
      ),
      startWith(0)
    )

  getGenericDistance$ = (): Observable<number> =>
    fromEvent(this.distanceMeasureSource, 'change').pipe(
      map((event: any) => {
        const vectorSource = event.target as VectorSource;
        const lengthFeatures = vectorSource
          .getFeatures()
          .map((feature) => feature.getGeometry())
          .filter((geometry) => geometry.getProperties()['type'] === 'GenericDistance');
        if (lengthFeatures.length > 0) {
          const totalDistance = lengthFeatures.reduce((acc, geometry) => {
            return acc + MeasureUtils.computeLength(geometry as LineString);
          }, 0);
          return totalDistance;
        }
        return 0;
      }),
      startWith(0)
  );

  getAllMeasuresForZone$ = (zoneId: number): Observable<ZoneMeasures> =>
    combineLatest([
      this.getAreaForZone$(zoneId),
      this.getAzimuthForZone$(zoneId),
      this.getExclusionsChange$(zoneId),
    ]).pipe(
      map(([surface, azimuth]) => {
        const zone = this.state.value.zones[zoneId];
        const vectorSource = zone.vectorLayer.getSource();

        const polygons = vectorSource
          .getFeatures()
          .filter(feature => feature.getGeometry()?.getType() === 'Polygon' && feature.getGeometry()?.getProperties()['type'] === ToolEnum.Surface)
          .map(feature => {
            const geometry = feature.getGeometry() as Polygon;
            const coordinates = geometry.getCoordinates()[0].map(coord => {
              const [x, y] = transform(coord, 'EPSG:3857', 'EPSG:4326');
              return { x, y } as CoordinateDto;
            });
            return { coordinates } as PolygonDto;
          });

        // Extract azimuth coordinates
        const azimuthFeature = vectorSource
          .getFeatures()
          .find(feature => feature.getGeometry()?.getType() === 'LineString' && feature.getGeometry()?.getProperties()['type'] === ToolEnum.Azimuth);
        let azimutCoordinates: CoordinateDto[] | null = null;
        if (azimuthFeature) {
          const geometry = azimuthFeature.getGeometry() as LineString;
          const coords = geometry.getCoordinates();
          if (coords.length >= 2) {
            azimutCoordinates = coords.map(coord => {
              const [x, y] = transform(coord, 'EPSG:3857', 'EPSG:4326');
              return { x, y } as CoordinateDto;
            });
          } else {
            console.warn(`Insufficient coordinates for azimuth feature in zone ${zoneId}:`, coords);
          }
        } else {
        }

        const exclusions = zone.exclusionLayer.getSource()
          .getFeatures()
          .filter(feature => feature.getGeometry()?.getType() === 'Polygon')
          .map(feature => {
            const geometry = feature.getGeometry() as Polygon;
            const coordinates = geometry.getCoordinates()[0].map(coord => {
              const [x, y] = transform(coord, 'EPSG:3857', 'EPSG:4326');
              return { x, y } as CoordinateDto;
            });
            return { id: (feature.getId() ?? feature.getProperties()['id'] ?? '').toString(), coordinates };
          });

        const panneaux = zone.panneauLayer.getSource()
          .getFeatures()
          .filter(feature => feature.getGeometry()?.getType() === 'Polygon')
          .map(feature => this.featureToPanneauDto(feature));

        return { surface, azimuth, polygons, azimutCoordinates, exclusions, panneaux };
      })
    );



  // Actions
  positionMap(coordinates: number[]): void {
    this.map.getView().setCenter(fromLonLat(coordinates));
  }

  positionGoogleMap(placeId: string): void {
    this.geocodingService.geoCodeGoogle(placeId).subscribe(
      data => {
        const coordinates: number[] = [data.lng,data.lat]
        this.map.getView().setCenter(fromLonLat(coordinates))
      }
    );
  }

  getDepartement$(lat: number, lng: number): Observable<ReverseGeoCodeGoogleDto> {
    return this.geocodingService.reverseGeoCodeGoogle(lat, lng)
  }

  disableTools(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)
    const select = new Select({
        layers: [this.currentZone.vectorLayer],
        filter: (feature) => {
            const type = feature.getGeometry()?.getProperties()['type']
            return type !== ToolEnum.MeterBt && type !== ToolEnum.TransformerHt
        }
    })

    // Sécurité : si on quitte une édition en cours (changement de zone/outil sans désélection),
    // on relance une dernière fois pour ne pas perdre les modifications, puis on repart propre.
    if (this.isEditingDrawing) {
        this.editCommitted$.next()
    }
    this.isEditingDrawing = false
    const modify = new Modify({
        features: select.getFeatures(),
    })
    // Perf calepinage : tant qu'un dessin est sélectionné (en cours d'édition), on suspend le
    // recalcul ; on le relance une seule fois à la DÉSÉLECTION (clic sur une zone vide de la carte).
    // Le résultat du calepinage est identique, seul le moment du recalcul change.
    select.on('select', () => {
        if (select.getFeatures().getLength() > 0) {
            this.isEditingDrawing = true
        } else if (this.isEditingDrawing) {
            this.isEditingDrawing = false
            this.editCommitted$.next()
        }
    })
    this.map.addInteraction(select)
    this.map.addInteraction(modify)
    this.state.next({ ...this.state.value, activeTool: ToolEnum.None })
}

  selectModeValorisationOutils(): void {
    this.state.next({ ...this.state.value, selectedModeValorisation: true })
    this.selectionBarreOutils()
  }

  selectTypePoseOutils(zoneIndex: number): void {
    const copySelectedTypePose = [...this.state.value.selectedTypePose]
    copySelectedTypePose[zoneIndex] = true
    this.state.next({ ...this.state.value, selectedTypePose: copySelectedTypePose })
  }

  deselectTypePoseOutils(zoneIndex: number): void {
    const copySelectedTypePose = [...this.state.value.selectedTypePose]
    copySelectedTypePose[zoneIndex] = false
    this.state.next({ ...this.state.value, selectedTypePose: copySelectedTypePose })
  }

  setZone(zoneIndex: number): void {
    const newZone = this.state.value.zones[zoneIndex]
    if (newZone) {
      const allLayers = this.map.getAllLayers()
      if (!allLayers.includes(newZone.vectorLayer)) this.map.addLayer(newZone.vectorLayer)
      if (!allLayers.includes(newZone.exclusionLayer)) this.map.addLayer(newZone.exclusionLayer)
      if (!allLayers.includes(newZone.panneauLayer)) this.map.addLayer(newZone.panneauLayer)
    }
    this.state.next({ ...this.state.value, activeZoneId: zoneIndex })
    this.disableTools()
  }

  resetCurrentZone(): void {
    this.currentZone.vectorLayer.getSource().clear()
    this.currentZone.exclusionLayer.getSource().clear()
    this.currentZone.panneauLayer.getSource().clear()
    this.currentZoneHasBeanReset$.next(this.activeZoneId)
    const selectedTypePoseIndex = this.getSelectedTypePoseIndex(this.activeZoneId)
    if (selectedTypePoseIndex !== null) {
      this.deselectTypePoseOutils(selectedTypePoseIndex)
    }
    this.deselectionDessinOutilsSeulementZone()
  }

  hiddenCurrentZone(): void {
    this.map.removeLayer(this.currentZone.vectorLayer)
    this.map.removeLayer(this.currentZone.exclusionLayer)
    this.map.removeLayer(this.currentZone.panneauLayer)
    this.state.next({ ...this.state.value, activeZoneId: null })
  }

  resetDessinModeValorisation(): void {
    this.currentZone.vectorLayer.getSource().clear()
  }

  resetDessinRaccordement(): void {
    this.state.value.zones[3].vectorLayer.getSource().clear()
    this.state.next({
      ...this.state.value,
      tableSeedLabels: [],
      activeTrenchLabel: null,
      validatedTrenchMode: null,
      manualTrenchRows: {},
    })
  }

  clearZoneDrawings(zoneIndex: number): void {
    const vectorSource = this.state.value.zones[zoneIndex]?.vectorLayer.getSource()
    vectorSource
      ?.getFeatures()
      .filter((feature) => feature.getGeometry()?.getProperties()['type'] === ToolEnum.Surface)
      .forEach((feature) => vectorSource.removeFeature(feature))
  }

  activateMeasureAreaTool(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { vectorLayer, color, typeVehiculeParking } = this.currentZone

    if (typeVehiculeParking === 'VL_PLACE_1' || typeVehiculeParking === 'VL_PLACE_1_BOIS') {
      this.map.addInteraction(
        InteractionUtils.buildBufferedLineAsAreaInteraction(vectorLayer.getSource(), color, this.width1place)
      )
    }
    else if(typeVehiculeParking === 'VL_PLACE_2' || typeVehiculeParking === 'VL_PLACE_2_BOIS' ) {
      this.map.addInteraction(
        InteractionUtils.buildBufferedLineAsAreaInteraction(vectorLayer.getSource(), color, this.width2place)
      )
    }     
    else if(typeVehiculeParking === 'VL_EST_OUEST') {
      this.map.addInteraction(
        InteractionUtils.buildBufferedLineAsAreaInteraction(vectorLayer.getSource(), color, this.widthEstOuest)
      )
    } else {
      this.map.addInteraction(
        InteractionUtils.buildAreaInteraction(vectorLayer.getSource(), color)
      )
    }

    this.state.next({ ...this.state.value, activeTool: ToolEnum.Surface })
  }

  resetUnfinishedDrawing(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)

    switch(this.state.value.activeTool) {
      case ToolEnum.Surface :
        this.activateMeasureAreaTool()
        break

      case ToolEnum.Azimuth :
        this.activateMeasureAzimuthTool()
        break

      case ToolEnum.Length :
        this.activateMeasureDistanceTool()
        break

      case ToolEnum.Exclusion :
        this.activateExclusionTool()
        break

      case ToolEnum.EnedisLine :
        this.activateEnedisDistanceTool()
        break

      case ToolEnum.PrivateTrenchEnrobee :
        this.activatePrivateTrenchEnrobeeTool()
        break

      case ToolEnum.PrivateTrenchNaturel :
        this.activatePrivateTrenchNaturelTool()
        break

      case ToolEnum.PrivateTrenchToiture :
        this.activatePrivateTrenchToitureTool()
        break

      case ToolEnum.MeterBt :
      case ToolEnum.TransformerHt :
        this.activateRaccordementSquareTool()
        break
      
      case ToolEnum.GenericDistance:
        this.activateMeasureDistanceToolGeneric();
      break;
    }
  }

  activateMeasureAzimuthTool(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { vectorLayer, color } = this.currentZone
    this.map.addInteraction(InteractionUtils.buildAzimuthInteraction(vectorLayer.getSource(), color))
    this.state.next({ ...this.state.value, activeTool: ToolEnum.Azimuth })
  }

  activateMeasureDistanceTool(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { vectorLayer, color } = this.currentZone
    this.map.addInteraction(InteractionUtils.buildLengthInteraction(vectorLayer.getSource(), color))
    this.state.next({ ...this.state.value, activeTool: ToolEnum.Length })
  }

  activateEnedisDistanceTool(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { vectorLayer } = this.currentZone
    this.map.addInteraction(InteractionUtils.buildTypedLengthInteraction(vectorLayer.getSource(), '#2563eb', ToolEnum.EnedisLine))
    this.state.next({ ...this.state.value, activeTool: ToolEnum.EnedisLine })
  }

  activatePrivateTrenchEnrobeeTool(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { vectorLayer } = this.currentZone
    const interaction = InteractionUtils.buildTypedLengthInteraction(vectorLayer.getSource(), '#000000', ToolEnum.PrivateTrenchEnrobee)
    this.attachZoneMetadataToInteraction(interaction, ToolEnum.PrivateTrenchEnrobee)
    this.map.addInteraction(interaction)
    this.state.next({ ...this.state.value, activeTool: ToolEnum.PrivateTrenchEnrobee })
  }

  activatePrivateTrenchNaturelTool(): void {
    if (this.state.value.validatedTrenchMode === 'public') {
      return
    }
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { vectorLayer } = this.currentZone
    const interaction = InteractionUtils.buildTypedLengthInteraction(
      vectorLayer.getSource(),
      '#000000',
      ToolEnum.PrivateTrenchNaturel,
      [18, 12]
    )
    this.attachZoneMetadataToInteraction(interaction, ToolEnum.PrivateTrenchNaturel)
    this.map.addInteraction(interaction)
    this.state.next({ ...this.state.value, activeTool: ToolEnum.PrivateTrenchNaturel })
  }

  activatePrivateTrenchToitureTool(): void {
    if (this.state.value.validatedTrenchMode === 'public') {
      return
    }
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { vectorLayer } = this.currentZone
    const interaction = InteractionUtils.buildTypedLengthInteraction(
      vectorLayer.getSource(),
      '#000000',
      ToolEnum.PrivateTrenchToiture,
      [6, 10]
    )
    this.attachZoneMetadataToInteraction(interaction, ToolEnum.PrivateTrenchToiture)
    this.map.addInteraction(interaction)
    this.state.next({ ...this.state.value, activeTool: ToolEnum.PrivateTrenchToiture })
  }

  activateRaccordementSquareTool(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { vectorLayer } = this.currentZone
    const currentCounterCount = vectorLayer.getSource().getFeatures().filter((feature) => {
      const type = feature.getGeometry()?.getProperties()['type']
      return type === ToolEnum.MeterBt || type === ToolEnum.TransformerHt
    }).length
    if (this.state.value.maxCounterCount > 0 && currentCounterCount >= this.state.value.maxCounterCount) {
      this.disableTools()
      return
    }
    const selectedRaccordementType = this.state.value.selectedRaccordementType
    const toolType = selectedRaccordementType === 'BT' ? ToolEnum.MeterBt : ToolEnum.TransformerHt
    const color = '#000000'
    const interaction = InteractionUtils.buildSquareInteraction(vectorLayer.getSource(), color, toolType)
    interaction.on('drawend', () => {
      const updatedCounterCount = vectorLayer.getSource().getFeatures().filter((feature) => {
        const type = feature.getGeometry()?.getProperties()['type']
        return type === ToolEnum.MeterBt || type === ToolEnum.TransformerHt
      }).length
      if (this.state.value.maxCounterCount > 0 && updatedCounterCount >= this.state.value.maxCounterCount) {
        this.disableTools()
      }
    })
    this.map.addInteraction(interaction)
    this.state.next({ ...this.state.value, activeTool: toolType, counterPlacementActive: true })
  }

  setSelectedRaccordementType(type: 'BT' | 'HT'): void {
    this.state.next({ ...this.state.value, selectedRaccordementType: type })
  }

  startCounterPlacement(maxCounterCount: number): void {
    this.state.next({ ...this.state.value, maxCounterCount, counterPlacementActive: true })
    this.activateRaccordementSquareTool()
  }

  setCounterPlacementLimit(maxCounterCount: number): void {
    this.state.next({ ...this.state.value, maxCounterCount })
  }

  startPrivateTrenchSelection(): void {
      this.state.next({
        ...this.state.value,
        trenchSelectionMode: 'private',
        pendingTrenchZoneIds: [],
        pendingTrenchIsPdl: false,
        validatedTrenchMode: null,
        activeTrenchLabel: null,
      })
    this.disableTools()
    this.activatePrivateZoneSelectionOnMap()
  }

  startPublicTrenchSelection(): void {
    this.clearPendingSelectionStyles()
    const tableSeedLabels = [...this.state.value.tableSeedLabels]
    if (tableSeedLabels.indexOf('Public') < 0) {
      tableSeedLabels.push('Public')
    }
      this.state.next({
        ...this.state.value,
        trenchSelectionMode: null,
        pendingTrenchZoneIds: [],
        pendingTrenchIsPdl: false,
        validatedTrenchMode: 'public',
        tableSeedLabels,
        activeTrenchLabel: 'Public',
    })
    this.disableTools()
  }

  startPtrSelection(): void {
    this.clearPendingSelectionStyles()
    const tableSeedLabels = [...this.state.value.tableSeedLabels]
    if (tableSeedLabels.indexOf('PDL') < 0) {
      tableSeedLabels.push('PDL')
    }
      this.state.next({
        ...this.state.value,
        trenchSelectionMode: null,
        pendingTrenchZoneIds: [],
        pendingTrenchIsPdl: false,
        validatedTrenchMode: 'ptr',
        tableSeedLabels,
        activeTrenchLabel: 'PDL',
    })
    this.disableTools()
  }

  togglePendingTrenchZone(zoneId: number): void {
    const isAlreadySelected = this.state.value.pendingTrenchZoneIds.indexOf(zoneId) >= 0
    const pending = isAlreadySelected
      ? this.state.value.pendingTrenchZoneIds.filter((pendingZoneId) => pendingZoneId !== zoneId)
      : [...this.state.value.pendingTrenchZoneIds, zoneId].sort((left, right) => left - right)
    this.state.next({
      ...this.state.value,
      pendingTrenchZoneIds: pending,
      pendingTrenchIsPdl: false,
    })
  }

  togglePendingTrenchZoneFromButton(zoneId: number): void {
    if (this.state.value.trenchSelectionMode !== 'private') {
      return
    }

    if (this.state.value.pendingTrenchIsPdl) {
      this.clearPendingSelectionStyles()
      this.state.next({
        ...this.state.value,
        pendingTrenchIsPdl: false,
        pendingTrenchZoneIds: [],
      })
    }

    this.togglePendingTrenchZone(zoneId)
    const isSelected = this.state.value.pendingTrenchZoneIds.indexOf(zoneId) >= 0
    this.applyPendingSelectionStyleForZone(zoneId, isSelected)
  }

  hasTrenchDrawings(label: string, field?: 'enrobee' | 'terrainNaturel' | 'passageToiture'): boolean {
    return this.state.value.zones[3].vectorLayer
      .getSource()
      .getFeatures()
      .some((feature) => {
        const geometry = feature.getGeometry()
        if (!(geometry instanceof LineString) || geometry.getProperties()['targetLabel'] !== label) {
          return false
        }
        if (!field) {
          return true
        }
        return this.isMatchingTrenchField(geometry.getProperties()['type'] as ToolEnum | undefined, field)
      })
  }

  clearTrenchDrawings(label: string, field?: 'enrobee' | 'terrainNaturel' | 'passageToiture'): void {
    const vectorSource = this.state.value.zones[3].vectorLayer.getSource()
    vectorSource
      .getFeatures()
      .filter((feature) => {
        const geometry = feature.getGeometry()
        if (!(geometry instanceof LineString) || geometry.getProperties()['targetLabel'] !== label) {
          return false
        }
        if (!field) {
          return true
        }
        return this.isMatchingTrenchField(geometry.getProperties()['type'] as ToolEnum | undefined, field)
      })
      .forEach((feature) => vectorSource.removeFeature(feature))
    vectorSource.changed()
  }

  updateManualTrenchRow(label: string, field: 'enrobee' | 'terrainNaturel' | 'passageToiture', value: number): void {
    const currentRow = this.state.value.manualTrenchRows[label] ?? {}
    const manualTrenchRows = {
      ...this.state.value.manualTrenchRows,
      [label]: {
        ...currentRow,
        [field]: value,
      },
    }
    this.state.next({ ...this.state.value, manualTrenchRows })
  }

  clearManualTrenchRow(label: string): void {
    if (!this.state.value.manualTrenchRows[label]) {
      return
    }
    const manualTrenchRows = { ...this.state.value.manualTrenchRows }
    delete manualTrenchRows[label]
    this.state.next({ ...this.state.value, manualTrenchRows })
  }

  clearManualTrenchField(label: string, field: 'enrobee' | 'terrainNaturel' | 'passageToiture'): void {
    const currentRow = this.state.value.manualTrenchRows[label]
    if (!currentRow || currentRow[field] == null) {
      return
    }

    const nextRow = { ...currentRow }
    delete nextRow[field]

    const manualTrenchRows = { ...this.state.value.manualTrenchRows }
    if (nextRow.enrobee == null && nextRow.terrainNaturel == null && nextRow.passageToiture == null) {
      delete manualTrenchRows[label]
    } else {
      manualTrenchRows[label] = nextRow
    }
    this.state.next({ ...this.state.value, manualTrenchRows })
  }

  togglePendingTrenchPdl(): void {
    if (this.state.value.tableSeedLabels.indexOf('PDL') >= 0) {
      return
    }
    this.state.next({
      ...this.state.value,
      pendingTrenchZoneIds: [],
      pendingTrenchIsPdl: !this.state.value.pendingTrenchIsPdl,
    })
  }

  confirmPendingTrenchSelection(): void {
    if (
      this.state.value.trenchSelectionMode === 'private' &&
      this.state.value.pendingTrenchZoneIds.length === 0 &&
      !this.state.value.pendingTrenchIsPdl
    ) {
      return
    }

    const labelsToAdd =
      this.state.value.trenchSelectionMode === 'public'
        ? ['Public']
        : this.state.value.trenchSelectionMode === 'ptr' || this.state.value.pendingTrenchIsPdl
          ? ['PDL']
          : [this.state.value.pendingTrenchZoneIds.join(' & ')]

    const tableSeedLabels = [...this.state.value.tableSeedLabels]
    labelsToAdd.forEach((label: string) => {
      if (tableSeedLabels.indexOf(label) < 0) {
        tableSeedLabels.push(label)
      }
    })

    this.state.next({
      ...this.state.value,
      validatedTrenchMode: this.state.value.pendingTrenchIsPdl ? 'ptr' : this.state.value.trenchSelectionMode,
      trenchSelectionMode: null,
      pendingTrenchIsPdl: false,
      tableSeedLabels,
      activeTrenchLabel: labelsToAdd[0] ?? null,
    })
    this.clearPendingSelectionStyles()
    this.disableTools()
  }

  openRaccordementTools(): void {
    this.selectModeValorisationOutils()
    this.selectionBarreOutils()
    this.deselectionDessinOutilsSeulementZone()
    this.setZone(3)
  }

  deleteTrenchRow(label: string): void {
    const vectorSource = this.state.value.zones[3].vectorLayer.getSource()
    vectorSource
      .getFeatures()
      .filter((feature) => feature.getGeometry()?.getProperties()['targetLabel'] === label)
      .forEach((feature) => vectorSource.removeFeature(feature))

    const tableSeedLabels = this.state.value.tableSeedLabels.filter((item) => item !== label)
    this.state.next({
      ...this.state.value,
      tableSeedLabels,
      activeTrenchLabel: this.state.value.activeTrenchLabel === label ? null : this.state.value.activeTrenchLabel,
      validatedTrenchMode:
        this.state.value.activeTrenchLabel === label ? null : this.state.value.validatedTrenchMode,
      manualTrenchRows: Object.keys(this.state.value.manualTrenchRows).reduce((acc: { [key: string]: { enrobee?: number; terrainNaturel?: number; passageToiture?: number } }, key) => {
        if (key !== label) {
          acc[key] = this.state.value.manualTrenchRows[key]
        }
        return acc
      }, {}),
    })
    vectorSource.changed()
  }

  selectTrenchRow(label: string): void {
    const isPublic = label === 'Public'
    const isPtr = label === 'PDL'
    this.state.next({
      ...this.state.value,
      activeTrenchLabel: label,
      validatedTrenchMode: isPublic ? 'public' : isPtr ? 'ptr' : 'private',
      trenchSelectionMode: null,
      pendingTrenchIsPdl: isPtr,
      pendingTrenchZoneIds: isPublic || isPtr
        ? []
        : label
            .split('&')
            .map((part) => Number(part.trim()))
            .filter((zoneId) => !Number.isNaN(zoneId)),
    })
  }

  private activatePrivateZoneSelectionOnMap(): void {
    this.clearPendingSelectionStyles()
    const selectInteraction = new Select({
      layers: [
        this.state.value.zones[0].vectorLayer,
        this.state.value.zones[1].vectorLayer,
        this.state.value.zones[2].vectorLayer,
        this.state.value.zones[4].vectorLayer,
        this.state.value.zones[5].vectorLayer,
        this.state.value.zones[6].vectorLayer,
        this.state.value.zones[3].vectorLayer,
      ],
      style: null,
      filter: (feature) => {
        const geometry = feature.getGeometry()
        if (!(geometry instanceof Polygon)) {
          return false
        }
        const type = geometry.getProperties()['type']
        if (type === ToolEnum.Surface) {
          const zoneId = this.getZoneIdFromPolygonFeature(feature as Feature)
          return zoneId !== null && !this.state.value.pendingTrenchIsPdl
        }
        if (type === ToolEnum.MeterBt || type === ToolEnum.TransformerHt) {
          return this.state.value.tableSeedLabels.indexOf('PDL') < 0 && this.state.value.pendingTrenchZoneIds.length === 0
        }
        return false
      },
    })

  selectInteraction.on('select', (event) => {
      event.selected.forEach((feature) => {
          const geometryType = feature.getGeometry()?.getProperties()['type']
          if (geometryType === ToolEnum.MeterBt || geometryType === ToolEnum.TransformerHt) {
              this.clearPendingSelectionStyles()
              this.togglePendingTrenchPdl()
              const isPdlNow = this.state.value.pendingTrenchIsPdl
              this.applyPendingSelectionStyle(feature as Feature, isPdlNow)
              return
          }
          const zoneId = this.getZoneIdFromPolygonFeature(feature as Feature)
          if (zoneId !== null && !this.state.value.pendingTrenchIsPdl) {
              this.togglePendingTrenchZone(zoneId)
              const isSelected = this.state.value.pendingTrenchZoneIds.indexOf(zoneId) >= 0
              this.applyPendingSelectionStyleForZone(zoneId, isSelected)
          }
      })
      selectInteraction.getFeatures().clear()
  })

    this.map.addInteraction(selectInteraction)
  }

  private attachZoneMetadataToInteraction(interaction: Draw, toolType: ToolEnum): void {
    interaction.on('drawend', (event) => {
      const feature = event.feature as Feature
      const geometry = feature.getGeometry() as LineString
      if (!(geometry instanceof LineString) || geometry.getProperties()['type'] !== toolType) {
        return
      }

      const targetLabel =
        this.state.value.validatedTrenchMode === 'public'
          ? 'Public'
          : this.state.value.validatedTrenchMode === 'ptr'
            ? 'PDL'
            : this.state.value.pendingTrenchZoneIds.join(' & ')
      const manualField =
        toolType === ToolEnum.PrivateTrenchNaturel
          ? 'terrainNaturel'
          : toolType === ToolEnum.PrivateTrenchToiture
            ? 'passageToiture'
            : 'enrobee'
      this.clearManualTrenchField(targetLabel, manualField)
      const firstZoneId = this.state.value.pendingTrenchZoneIds[0] ?? this.getZoneIdFromStartCoordinate(geometry.getFirstCoordinate())
      geometry.setProperties({
        ...geometry.getProperties(),
        zoneId: firstZoneId,
        color: '#000000',
        targetLabel,
      })
      feature.setStyle(this.buildTrenchFeatureStyle(toolType))
      this.currentZone.vectorLayer.getSource().changed()
    })
  }

  private getZoneIdFromPolygonFeature(feature: Feature): number | null {
    const geometry = feature.getGeometry()
    if (!(geometry instanceof Polygon)) {
      return null
    }

    const zoneIndexes = [0, 1, 2, 4, 5, 6]
    for (const index of zoneIndexes) {
      const matches = this.state.value.zones[index].vectorLayer
        .getSource()
        .getFeatures()
        .some((zoneFeature) => zoneFeature === feature)
      if (matches) {
        return index < 3 ? index + 1 : index
      }
    }

    return null
  }

  private getReservedPrivateZoneIds(): number[] {
    return this.state.value.tableSeedLabels
      .filter((label) => label !== 'Public' && label !== 'PDL')
      .reduce((acc: number[], label) => {
        label
          .split('&')
          .map((part) => Number(part.trim()))
          .filter((zoneId) => !Number.isNaN(zoneId))
          .forEach((zoneId) => acc.push(zoneId))
        return acc
      }, [])
  }

  private getTrenchTypeEnum(toolType: ToolEnum): TrenchTypeEnum {
    if (toolType === ToolEnum.PrivateTrenchNaturel) {
      return 'TERRAIN_NATUREL'
    }
    if (toolType === ToolEnum.PrivateTrenchToiture) {
      return 'PASSAGE_TOITURE'
    }
    return 'ENROBEE'
  }

  private buildTrenchFeatureStyle(toolType: ToolEnum): Style {
    const lineDash =
      toolType === ToolEnum.PrivateTrenchNaturel
        ? [18, 12]
        : toolType === ToolEnum.PrivateTrenchToiture
          ? [6, 10]
          : undefined

    return new Style({
      stroke: new Stroke({
        color: '#000000',
        width: 5,
        lineDash,
        lineCap: 'round',
      }),
    })
  }

  private trenchLabelToTypes(label: string): TrenchLineTypeEnum[] {
    if (label === 'Public') {
      return ['PUBLIC']
    }
    if (label === 'PDL') {
      return ['PDL']
    }
    return label
      .split('&')
      .map((part) => `ZONE${Number(part.trim())}` as TrenchLineTypeEnum)
      .filter((type) => type === 'ZONE1' || type === 'ZONE2' || type === 'ZONE3')
  }

  private trenchTypesToLabel(types: TrenchLineTypeEnum[] | string | undefined): string {
    if (!types) {
      return ''
    }
    if (!Array.isArray(types)) {
      return types
    }
    if (types.indexOf('PUBLIC') >= 0) {
      return 'Public'
    }
    if (types.indexOf('PDL') >= 0) {
      return 'PDL'
    }
    return types
      .map((type) => type.replace('ZONE', ''))
      .join(' & ')
  }

  private getZoneIdFromStartCoordinate(coordinate: number[]): number | null {
    const zoneIndexes = [0, 1, 2, 4, 5, 6]
    for (const index of zoneIndexes) {
      const polygons = this.state.value.zones[index].vectorLayer
        .getSource()
        .getFeatures()
        .filter((feature) => feature.getGeometry() instanceof Polygon && feature.getGeometry().getProperties()['type'] === ToolEnum.Surface)

      if (polygons.some((feature) => (feature.getGeometry() as Polygon).intersectsCoordinate(coordinate))) {
        return index < 3 ? index + 1 : index
      }
    }

    return null
  }

  activateDeleteTool(): void {
    MapUtils.resetMapToDefaultInteractions(this.map);
    const { vectorLayer, exclusionLayer, panneauLayer } = this.currentZone;

    const select = new Select({
      layers: [
        vectorLayer,
        exclusionLayer,
        panneauLayer,
        this.distanceMeasureLayer,
        this.raccordementPublicLayer,
      ],
    });

    select.on('select', event => {
      const selectedFeature = event.selected[0];
      if (selectedFeature) {
        if (this.distanceMeasureSource.getFeatures().includes(selectedFeature)) {
          this.distanceMeasureSource.removeFeature(selectedFeature);
        } else if (exclusionLayer.getSource().getFeatures().includes(selectedFeature)) {
          exclusionLayer.getSource().removeFeature(selectedFeature);
        } else if (panneauLayer.getSource().getFeatures().includes(selectedFeature)) {
          panneauLayer.getSource().removeFeature(selectedFeature);
        } else if (vectorLayer.getSource().getFeatures().includes(selectedFeature)) {
          vectorLayer.getSource().removeFeature(selectedFeature);
        } else if (this.raccordementPublicSource.getFeatures().includes(selectedFeature)) {
          this.raccordementPublicSource.removeFeature(selectedFeature);
        }
        select.getFeatures().clear();
      }
    });

    this.map.addInteraction(select);

    // Shift+drag = suppression en masse de panneaux dans le rectangle sélectionné.
    this.map.addInteraction(
      InteractionUtils.buildPanneauDragDeleteInteraction(panneauLayer.getSource())
    );

    this.state.next({ ...this.state.value, activeTool: ToolEnum.Delete });
  }

  toggleCadastreMode(): void {
    const currentState = this.state.value;
    const newCadastreState = !currentState.isCadastreEnabled;
    this.state.next({ ...this.state.value, isCadastreEnabled: newCadastreState });

    MapUtils.updateMapLayers(this.map, newCadastreState);
  }

  activateMeasureDistanceToolGeneric(): void {
    MapUtils.resetMapToDefaultInteractions(this.map);
    const draw = InteractionUtils.buildGenericDistanceInteraction(this.distanceMeasureSource);
    this.map.addInteraction(draw);
    draw.on('drawstart', (event) => {
      const geometry = event.feature.getGeometry();
      geometry.setProperties({ type: 'GenericDistance' }); 
    });
    draw.on('drawend', () => {
      this.distanceMeasureSource.changed();
    });
    this.state.next({ ...this.state.value, activeTool: ToolEnum.GenericDistance });
  }

  activateExclusionTool(): void {
    MapUtils.resetMapToDefaultInteractions(this.map)
    const { exclusionLayer } = this.currentZone
    this.map.addInteraction(InteractionUtils.buildExclusionInteraction(exclusionLayer.getSource()))
    this.state.next({ ...this.state.value, activeTool: ToolEnum.Exclusion })
  }

  private applyPendingSelectionStyle(feature: Feature, isSelected: boolean): void {
    if (!isSelected) {
      const previousStyle = feature.get('pendingSelectionOriginalStyle')
      feature.setStyle(previousStyle ?? undefined)
      feature.unset('pendingSelectionOriginalStyle', true)
      return
    }

    if (feature.get('pendingSelectionOriginalStyle') === undefined) {
      feature.set('pendingSelectionOriginalStyle', feature.getStyle() ?? null)
    }

    const geometryType = feature.getGeometry()?.getProperties()['type']
    if (geometryType === ToolEnum.MeterBt || geometryType === ToolEnum.TransformerHt) {
      feature.setStyle(new Style({
        fill: new Fill({
          color: '#000000',
        }),
        stroke: new Stroke({
          color: '#facc15',
          width: 3,
        }),
      }))
      return
    }

    const hexToRgba = (hex: string, alpha: number): string => {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    const zoneId = this.getZoneIdFromPolygonFeature(feature)
    const zoneIndex = zoneId !== null ? this.getZoneIndexFromZoneId(zoneId) : null
    const zone = zoneIndex !== null ? this.state.value.zones[zoneIndex] : null
    feature.setStyle(new Style({
      fill: new Fill({
        color: zone?.color ? hexToRgba(zone.color, 0.5) : 'rgba(0, 0, 0, 0.2)',
      }),
      stroke: new Stroke({
        color: zone?.color || '#000000',
        width: 5,
      }),
    }))
  }

  private isMatchingTrenchField(
    toolType: ToolEnum | undefined,
    field: 'enrobee' | 'terrainNaturel' | 'passageToiture'
  ): boolean {
    if (field === 'terrainNaturel') {
      return toolType === ToolEnum.PrivateTrenchNaturel
    }
    if (field === 'passageToiture') {
      return toolType === ToolEnum.PrivateTrenchToiture
    }
    return toolType === ToolEnum.PrivateTrenchEnrobee || toolType === ToolEnum.EnedisLine
  }

  private applyPendingSelectionStyleForZone(zoneId: number, isSelected: boolean): void {
    const zoneIndex = this.getZoneIndexFromZoneId(zoneId)
    if (zoneIndex === null) {
      return
    }
    this.state.value.zones[zoneIndex].vectorLayer
      .getSource()
      .getFeatures()
      .filter((feature) => feature.getGeometry() instanceof Polygon && feature.getGeometry()?.getProperties()['type'] === ToolEnum.Surface)
      .forEach((feature) => this.applyPendingSelectionStyle(feature, isSelected))
  }

  private getZoneIndexFromZoneId(zoneId: number): number | null {
    if ([1, 2, 3].includes(zoneId)) {
      return zoneId - 1
    }
    if ([4, 5, 6].includes(zoneId)) {
      return zoneId
    }
    return null
  }

  private getSelectedTypePoseIndex(zoneIndex: number): number | null {
    if ([0, 1, 2].includes(zoneIndex)) {
      return zoneIndex
    }
    if ([4, 5, 6].includes(zoneIndex)) {
      return zoneIndex - 1
    }
    return null
  }

  private clearPendingSelectionStyles(): void {
    this.state.value.zones.forEach((zone) => {
      zone.vectorLayer.getSource().getFeatures().forEach((feature) => {
        const previousStyle = feature.get('pendingSelectionOriginalStyle')
        if (previousStyle !== undefined) {
          feature.setStyle(previousStyle ?? undefined)
          feature.unset('pendingSelectionOriginalStyle', true)
        }
      })
    })
  }

  activateTooltips(tooltipElementRef: ElementRef): void {
    const tooltipElement = tooltipElementRef.nativeElement
    tooltipElement.style.pointerEvents = 'none'
    tooltipElement.style.userSelect = 'none'
    tooltipElement.style.webkitUserSelect = 'none'
    const overlay = new Overlay({
      element: tooltipElement,
      offset: [0, 0],
      positioning: 'center-center',
      stopEvent: false,
    })

    this.map.on('pointermove', (evt: MapBrowserEvent<PointerEvent>) => {
      overlay.setPosition(null)
      this.map.forEachFeatureAtPixel(evt.pixel, (feature: FeatureLike) => {
        if (feature.getGeometry().getType() !== 'Point') {
          tooltipElement.innerHTML = TooltipUtils.getTooltipLabel(feature.getGeometry() as Geometry)
          overlay.setPosition(TooltipUtils.getTooltipPosition(feature.getGeometry() as Geometry))
        }
      })
    })
    this.activeTool$.subscribe((tool) => {
      const currentInteraction = this.map.getInteractions().item(this.map.getInteractions().getLength() - 1)
      if (currentInteraction instanceof Draw) {
        currentInteraction.on('drawstart', (event) => {
          event.feature.getGeometry().on('change', (event) => {
            overlay.setPosition(TooltipUtils.getTooltipPosition(event.target))
            tooltipElement.innerHTML = TooltipUtils.getTooltipLabel(event.target)
          })
        })
        this.map.addOverlay(overlay)
        currentInteraction.on('drawend', (event) => {
          overlay.setPosition(null)
        })
      }
    })
  }

  selectionBarreOutils(): void {
    this.state.next({ ...this.state.value, selectedBarreOutils: true })
  }

  deselectionBarreOutils(): void {
    this.state.next({ ...this.state.value, selectedBarreOutils: false })
  }

  selectionDessinOutilsSeulementZone(): void {
    this.state.next({ ...this.state.value, selectedDessinOutilsSeulementZone: true })
  }

  deselectionDessinOutilsSeulementZone(): void {
    this.state.next({ ...this.state.value, selectedDessinOutilsSeulementZone: false })
  }

drawZoneShapes(zoneId: number, zoneData: SessionZoneSaveDto): void {

  if (zoneId < 0 || zoneId >= this.state.value.zones.length) {
    console.error(`Invalid zoneId: ${zoneId}. Available zones: ${this.state.value.zones.length}`);
    return;
  }

  this.setZone(zoneId);

  const vectorSource = this.state.value.zones[zoneId].vectorLayer.getSource();
  const color = this.state.value.zones[zoneId].color || '#000000';

  vectorSource.clear();

  if (zoneData.polygons && zoneData.polygons.length > 0) {
    zoneData.polygons.forEach((polygonDto: PolygonDto, index: number) => {
      if (!polygonDto.coordinates || polygonDto.coordinates.length < 3) {
        console.warn(`Invalid polygon at index ${index} for zone ${zoneId}:`, polygonDto);
        return;
      }

      const coordinates = polygonDto.coordinates.map((coord: CoordinateDto) => {
        const transformed = fromLonLat([coord.x, coord.y]);
        return transformed;
      });

      const polygon = new Polygon([coordinates]);
      polygon.setProperties({ type: ToolEnum.Surface });

      const feature = new Feature({
        geometry: polygon,
      });
      // Pas de setStyle ici : on laisse le style du layer s'appliquer (plain surface),
      // sinon le motif "panneaux décoratifs" ressort à la restauration.

      vectorSource.addFeature(feature);
    });
  }

  if (zoneData.azimutCoordinates && zoneData.azimutCoordinates.length >= 2 && zoneData.azimut != null) {
    const coords = zoneData.azimutCoordinates.map(coord => {
      const transformed = fromLonLat([coord.x, coord.y]);
      return transformed;
    });

    const line = new LineString(coords);
    line.setProperties({ type: ToolEnum.Azimuth, color });

    const feature = new Feature({
      geometry: line,
    });
    const azimuthStyles = InteractionUtils.buildAzimuthArrowStyle(feature) || [];
    feature.setStyle([
      InteractionUtils.buildDefaultStyleFromColor(color),
      ...azimuthStyles,
    ]);

    vectorSource.addFeature(feature);
  }

  const panneauSource = this.state.value.zones[zoneId].panneauLayer.getSource();
  panneauSource.clear();
  if (zoneData.panneaux && zoneData.panneaux.length > 0) {
    zoneData.panneaux.forEach((panneauDto: PanneauDto, index: number) => {
      if (!panneauDto.coordinates || panneauDto.coordinates.length < 3) {
        console.warn(`Invalid panneau at index ${index} for zone ${zoneId}:`, panneauDto);
        return;
      }
      panneauSource.addFeature(this.panneauDtoToFeature(panneauDto));
    });
  }

  const panneauLayer = this.state.value.zones[zoneId].panneauLayer;
  if (!this.map.getLayers().getArray().includes(panneauLayer)) {
    this.map.addLayer(panneauLayer);
  }

  const layer = this.state.value.zones[zoneId].vectorLayer;
  if (!this.map.getLayers().getArray().includes(layer)) {
    this.map.addLayer(layer);
  } else {
  }

  vectorSource.changed();
  if (vectorSource.getFeatures().length > 0) {
    const extent = vectorSource.getExtent();
    this.map.getView().fit(extent, {
      padding: [50, 50, 50, 50],
      maxZoom: 19,
      duration: 500,
    });
  } else {
    console.log(`No features added for zone ${zoneId}, skipping map view fit`);
  }
}

drawRaccordementPublic(raccordementData: RaccordementPublic): void {
  const zoneId = 3;
  if (zoneId < 0 || zoneId >= this.state.value.zones.length) {
    console.error(`Invalid zoneId: ${zoneId}. Available zones: ${this.state.value.zones.length}`);
    return;
  }

  this.setZone(zoneId);

  const vectorSource = this.state.value.zones[zoneId].vectorLayer.getSource();
  vectorSource.clear();
  this.state.next({ ...this.state.value, tableSeedLabels: [], activeTrenchLabel: null, validatedTrenchMode: null, manualTrenchRows: {} });

  const addLineFeatures = (
    polygons: PolygonDto[] | undefined,
    toolType: ToolEnum,
    strokeColor: string,
    targetLabel?: string
  ): void => {
    if (!polygons?.length) {
      return;
    }

      polygons.forEach((polygonDto: PolygonDto, index: number) => {
        if (polygonDto.coordinates && polygonDto.coordinates.length >= 2) {
          const coordinates = polygonDto.coordinates.map((coord: CoordinateDto) => fromLonLat([coord.x, coord.y]));
          const lineGeometry = new LineString(coordinates);
          const zoneId = this.getZoneIdFromStartCoordinate(coordinates[0]);
        lineGeometry.setProperties({ type: toolType, color: '#000000', zoneId, targetLabel });

        const feature = new Feature({
          geometry: lineGeometry,
        });
        feature.setStyle(this.buildTrenchFeatureStyle(toolType));

        vectorSource.addFeature(feature);
      } else {
        console.warn(`Invalid line segment at index ${index} for raccordementPublic:`, polygonDto);
      }
    });
  };

  const addSquareFeature = (polygonDto: PolygonDto | null | undefined, toolType: ToolEnum, strokeColor: string): void => {
    if (!polygonDto?.coordinates?.length) {
      return;
    }

    const coordinates = polygonDto.coordinates.map((coord: CoordinateDto) => fromLonLat([coord.x, coord.y]));
    const polygon = new Polygon([coordinates]);
    polygon.setProperties({ type: toolType, color: strokeColor });

    const feature = new Feature({
      geometry: polygon,
    });
    feature.setStyle(new Style({
      stroke: new Stroke({
        color: strokeColor,
        width: 3,
      }),
    }));

    vectorSource.addFeature(feature);
  };

  const addCounterSquareFromCenter = (center: CoordinateDto): void => {
    const sourceProjectionCenter = fromLonLat([center.x, center.y])
    const [x, y] = sourceProjectionCenter
    const halfSize = 3
    const polygon = new Polygon([[
      [x - halfSize, y - halfSize],
      [x + halfSize, y - halfSize],
      [x + halfSize, y + halfSize],
      [x - halfSize, y + halfSize],
      [x - halfSize, y - halfSize],
    ]])
    polygon.setProperties({
      type: this.state.value.selectedRaccordementType === 'HT' ? ToolEnum.TransformerHt : ToolEnum.MeterBt,
      color: '#000000'
    })

    const feature = new Feature({
      geometry: polygon,
    })
    feature.setStyle(new Style({
      stroke: new Stroke({
        color: '#000000',
        width: 2,
      }),
      fill: new Fill({
        color: '#000000',
      }),
    }))
    vectorSource.addFeature(feature)
  }

    if (raccordementData.trenchCoordinates?.length) {
      const tableSeedLabels: string[] = []
      const manualTrenchRows: { [key: string]: { enrobee?: number; terrainNaturel?: number; passageToiture?: number } } = {}
      raccordementData.trenchCoordinates.forEach((trenchLine) => {
        const targetLabel = this.trenchTypesToLabel(trenchLine.type as TrenchLineTypeEnum[] | string | undefined)
        if (targetLabel && tableSeedLabels.indexOf(targetLabel) < 0) {
          tableSeedLabels.push(targetLabel)
        }
        if (targetLabel) {
          const manualRow: { enrobee?: number; terrainNaturel?: number; passageToiture?: number } = {}
          if (trenchLine.enrobee != null) {
            manualRow.enrobee = Number(trenchLine.enrobee)
          }
          if (trenchLine.terrainNaturel != null) {
            manualRow.terrainNaturel = Number(trenchLine.terrainNaturel)
          }
          if (trenchLine.passageToiture != null) {
            manualRow.passageToiture = Number(trenchLine.passageToiture)
          }
          manualTrenchRows[targetLabel] = manualRow
        }
        trenchLine.polygons?.forEach((trenchCoordinate) => {
          const toolType =
            trenchCoordinate.type === 'TERRAIN_NATUREL'
              ? ToolEnum.PrivateTrenchNaturel
              : trenchCoordinate.type === 'PASSAGE_TOITURE'
                ? ToolEnum.PrivateTrenchToiture
                : ToolEnum.PrivateTrenchEnrobee
          addLineFeatures(trenchCoordinate.polygons, toolType, '#000000', targetLabel)
        })
      })
      this.state.next({ ...this.state.value, tableSeedLabels, manualTrenchRows })
  } else {
    addLineFeatures(
      raccordementData.voirieEnedisCoordinates,
      ToolEnum.EnedisLine,
      '#000000',
      'Public'
    );
    addLineFeatures(raccordementData.trancheeEnrobeeCoordinates, ToolEnum.PrivateTrenchEnrobee, '#000000');
    addLineFeatures(raccordementData.trancheeTerrainNaturelCoordinates, ToolEnum.PrivateTrenchNaturel, '#000000');
    addLineFeatures(raccordementData.trancheeToitureCoordinates, ToolEnum.PrivateTrenchToiture, '#000000');
  }
  raccordementData.compteursCoordinates?.forEach((center) => addCounterSquareFromCenter(center));

  vectorSource.changed();

  // if (vectorSource.getFeatures().length > 0) {
  //   const extent = vectorSource.getExtent();
  //   this.map.getView().fit(extent, {
  //     padding: [50, 50, 50, 50],
  //     maxZoom: 19,
  //     duration: 500,
  //   });
  // } else {
  //   console.log(`No raccordementPublic features added, skipping map view fit`);
  // }
}

toggleEnedisMode(): void {
    const currentState = this.state.value;
    const newEnedisState = !currentState.isEnedisEnabled;
    this.state.next({ ...this.state.value, isEnedisEnabled: newEnedisState });

    const enedisLayer = this.map.getLayers().getArray().find(l => (l as any).get('name') === 'Enedis BT Souterraines') as VectorLayer<VectorSource>;
    if (enedisLayer) {
      if (newEnedisState) {
        enedisLayer.setVisible(true);
        MapUtils.updateEnedisBTVisible(this.map, enedisLayer);
      } else {
        enedisLayer.setVisible(false);
        enedisLayer.getSource()?.clear();
      }
    }
  }

  togglePostesMode(): void {
    const newState = !this.state.value.isPostesEnabled;
    this.state.next({ ...this.state.value, isPostesEnabled: newState });

    const layer = this.map.getLayers().getArray()
      .find(l => (l as any).get('name') === 'Postes HTA/BT') as VectorLayer<VectorSource>;

    if (layer) {
      layer.setVisible(newState);
      if (newState) {
        MapUtils.updatePostesVisible(this.map, layer);
      } else {
        layer.getSource()?.clear();
      }
    }
  }

  togglePostesHtaHtaMode(): void {
    const newState = !this.state.value.isPostesHtaHtaEnabled;
    this.state.next({ ...this.state.value, isPostesHtaHtaEnabled: newState });

    const layer = this.map.getLayers().getArray()
      .find(l => (l as any).get('name') === 'Postes HTA/HTA') as VectorLayer<VectorSource>;

    if (layer) {
      layer.setVisible(newState);
      if (newState) {
        MapUtils.updatePostesHtaHtaVisible(this.map, layer);
      } else {
        layer.getSource()?.clear();
      }
    }
  }

  toggleHtaMode(): void {
    const currentState = this.state.value;
    const newHtaState = !currentState.isHtaEnabled;
    this.state.next({ ...this.state.value, isHtaEnabled: newHtaState });

    const htaLayer = this.map.getLayers().getArray().find(l => (l as any).get('name') === 'Enedis HTA Souterraines') as VectorLayer<VectorSource>;
    if (htaLayer) {
      if (newHtaState) {
        htaLayer.setVisible(true);
        MapUtils.updateHtaVisible(this.map, htaLayer);
      } else {
        htaLayer.setVisible(false);
        htaLayer.getSource()?.clear();
      }
    }
  }

}

export interface GeomapStateModel {
  map: Map
  zones: Zone[]
  activeZoneId: number
  activeTool: ToolEnum
  selectedModeValorisation: boolean
  selectedTypePose: boolean[],
  selectedBarreOutils: boolean
  selectedDessinOutilsSeulementZone: boolean
  isEnedisEnabled: boolean;
  isHtaEnabled: boolean;
  isPostesEnabled: boolean;
  isPostesHtaHtaEnabled: boolean;
  selectedRaccordementType: 'BT' | 'HT'
  counterPlacementActive: boolean
  maxCounterCount: number
  trenchSelectionMode: 'private' | 'public' | 'ptr' | null
  pendingTrenchZoneIds: number[]
  pendingTrenchIsPdl: boolean
  validatedTrenchMode: 'private' | 'public' | 'ptr' | null
  tableSeedLabels: string[]
  activeTrenchLabel: string | null
  manualTrenchRows: { [key: string]: { enrobee?: number; terrainNaturel?: number; passageToiture?: number } }
  isCadastreEnabled: boolean

}
